import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Loader2, Play, Workflow } from 'lucide-react';
import { api, errorMessage } from '../api/client';
import { useMe, useRunnableWorkflows } from '../api/hooks';
import { Modal } from './Modal';
import { toast } from './Toasts';
import { RightsCheckBlock, preflightBlocks, preflightRestorable, usePreflight, useRightsCheck, type RightsCheck } from './Preflight';
import { isBlockingProblem } from './rightsAck';

/**
 * Start a published workflow on this asset or sequence, e.g. an export or
 * delivery workflow. Lists enabled workflows with a manual trigger that fit.
 */
export function RunWorkflowButton({ subject, id, name, disabled = false }: { subject: 'asset' | 'sequence'; id: string; name: string; disabled?: boolean }) {
  const me = useMe().data!;
  const [open, setOpen] = useState(false);
  if (!me.permissions.includes('automation.run')) return null;
  return (
    <>
      {/* In the editor's top bar the label goes below 1680 px wide, as the editor's own (1.0.0-rc.6: room for Send to edit). */}
      <button className="btn sm" onClick={() => setOpen(true)} disabled={disabled} data-testid="run-workflow" title={`Run a workflow on this ${subject}`} aria-label="Run workflow">
        <Workflow size={14} /> <span className="btn-label">Run workflow</span>
      </button>
      {open && <RunWorkflowDialog subject={subject} id={id} name={name} onClose={() => setOpen(false)} />}
    </>
  );
}

/** One workflow that can be started, with what stops it (preflight: the media its steps read and where they deliver). */
function RunnableRow({ w, subject, subjectId, busy, onRun }: {
  w: { id: string; name: string; description: string; steps: string[] }; subject: 'asset' | 'sequence'; subjectId: string; busy: string | null; onRun: (rights: RightsCheck, restore?: boolean) => void;
}) {
  const preflight = usePreflight({ kind: 'workflow', workflowId: w.id, ...(subject === 'asset' ? { assetId: subjectId } : { sequenceId: subjectId }) });
  // 1.8.0: the rights problems a user answers are in the rights block below; the rest stop the run.
  const rights = useRightsCheck(preflight.data?.problems);
  const problems = (preflight.data?.problems ?? []).filter(isBlockingProblem);
  return (
    <div className="step" data-testid="runnable-workflow" data-name={w.name}>
      <span className={`step-icon ${problems.length ? 'failed' : 'succeeded'}`}>{problems.length ? <AlertTriangle size={12} /> : <Workflow size={12} />}</span>
      <div className="step-main">
        <span className="step-title">{w.name}</span>
        <span className="step-sub">{w.description || w.steps.join(' → ') || 'No steps'}</span>
        {problems.map((p, i) => <span key={i} className="step-sub" style={{ color: p.code === 'ORIGINAL_ARCHIVED' ? 'var(--warning)' : 'var(--danger)' }} data-testid="workflow-preflight-problem" data-code={p.code}>{p.message}</span>)}
        <RightsCheckBlock check={rights} compact />
      </div>
      {preflightRestorable(preflight) && (
        <button className="btn sm" disabled={busy !== null || !rights.ready} onClick={() => onRun(rights, true)} data-testid={`restore-start-${w.name}`} title="Restore the archived original first; the run waits for it">
          {busy === w.id ? <Loader2 size={13} className="spin" /> : <Play size={13} />} Restore and start
        </button>
      )}
      <button className="btn sm primary" disabled={busy !== null || preflightBlocks(preflight) || !rights.ready} onClick={() => onRun(rights)} data-testid={`start-${w.name}`}
        title={problems.length ? 'The media or destination this workflow needs is not available' : undefined}>
        {busy === w.id || preflight.isLoading ? <Loader2 size={13} className="spin" /> : <Play size={13} />} Run
      </button>
    </div>
  );
}

function RunWorkflowDialog({ subject, id, name, onClose }: { subject: 'asset' | 'sequence'; id: string; name: string; onClose: () => void }) {
  const q = useRunnableWorkflows(subject);
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const items = q.data?.items ?? [];

  async function run(workflowId: string, workflowName: string, rights: RightsCheck, restore = false) {
    setBusy(workflowId);
    try {
      const r = await api<{ runId: string }>(`/api/v1/automations/${workflowId}/runs`, { method: 'POST', json: { ...(subject === 'asset' ? { assetId: id } : { sequenceId: id }), ...(restore ? { restore: true } : {}), ...rights.field() } });
      onClose();
      toast(`Started “${workflowName}” on ${name}`, 'info', { label: 'Open run', onClick: () => navigate(`/workflows/runs/${r.runId}`) });
    } catch (err) {
      toast(rights.takeError(err) ? 'Check the rights first' : errorMessage(err), 'error');
      setBusy(null);
    }
  }

  return (
    <Modal title={`Run a workflow on ${subject === 'asset' ? 'this asset' : 'this sequence'}`} onClose={onClose} actions={<button className="btn ghost" onClick={onClose}>Close</button>}>
      {q.isLoading ? <div className="skeleton" style={{ height: 80 }} /> : items.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          No workflows can be started on {subject === 'asset' ? 'assets' : 'sequences'} yet. In Flows, publish a workflow whose trigger is “Manual run”
          {subject === 'sequence' ? ' started on a sequence (for example with Export sequence or Export EDL steps)' : ''}, and enable it.
        </p>
      ) : (
        <div className="steps" data-testid="runnable-workflows">
          {items.map((w) => <RunnableRow key={w.id} w={w} subject={subject} subjectId={id} busy={busy} onRun={(rights, restore) => void run(w.id, w.name, rights, restore)} />)}

        </div>
      )}
    </Modal>
  );
}
